import { useAuthStore } from '@/stores/authStore';
import type { Conversation, ConversationPriority, FolderPageResponse, PreviewSender, SessionDto, SessionState } from '@/types';

/**
 * Maps a SessionDto (GET /api/sessions) into the UI Conversation shape the
 * store + views consume. Backend states NEW/ACTIVE/CONSULTING/HUMAN_CONTROLLED/
 * CLOSED collapse to the UI's 4-value enum (NEW folds into AI_HANDLING for the
 * badge — the "New" distinction is carried by the ADR-017 Category, which is
 * what the inbox groups by). Extracted from the original DashboardPage so the
 * redesigned shell/pages can reuse it without importing a page component.
 */
export function mapSessionToConversation(
  s: SessionDto,
  /**
   * What the surrounding list envelope said (`FolderPageResponse.ownershipEnforced`). Only a fallback for a row that
   * does not carry its own value: the two come from the same server answer, so they cannot legitimately disagree.
   */
  envelope?: { ownershipEnforced?: boolean },
): Conversation {
  const state: SessionState =
    s.state === 'HUMAN_CONTROLLED'
      ? 'HUMAN_TAKEOVER'
      : s.state === 'CONSULTING'
      ? 'CONSULTATION_REQUESTED'
      : s.state === 'CLOSED'
      ? 'CLOSED'
      : 'AI_HANDLING';

  return {
    sessionId: s.sessionId,
    // SessionDto no longer carries tenantId on the wire (2026-08-09 leak closure) —
    // GET /api/sessions is already scoped to the caller's own tenant, so the
    // authenticated agent's own tenant claim is the trustworthy source.
    tenantId: useAuthStore.getState().user?.tenantId ?? '',
    customerId: s.customerId,
    customerDisplayName: s.customerDisplayName ?? undefined,
    firstMessagePreview: s.firstMessagePreview ?? undefined,
    // Normalised to null rather than undefined so "nobody named this" is one value everywhere,
    // matching the backend's own single representation of cleared.
    subject: s.subject ?? null,
    channel: s.channelType,
    state,
    slaUrgency: 'NORMAL',
    slaDeadlineUtc: s.lastMessageAt ?? s.createdAt,
    assignedAgentId: s.assignedAgentId ?? undefined,
    lastMessageContent: s.lastMessagePreview ?? '',
    lastMessageTimestamp: s.lastMessageAt ?? s.createdAt,
    unreadCount: 0,
    createdAt: s.createdAt,
    closedAt: s.closedAt ?? null,
    metadata: s.metadata ?? {},
    category: s.category ?? null,
    messageCount: s.messageCount,

    // CONTRACT-C (ADR-033). The console renders these; it never derives them.
    // `undefined` is kept distinct from `null` for the owner: null = "the server said nobody owns it", undefined =
    // "the server did not say" - unknown, and unknown fails closed everywhere downstream. There is deliberately NO
    // fallback to `assignedAgentId` here or in utils/ownership.ts.
    ownerAgentId: s.ownerAgentId === undefined ? undefined : (s.ownerAgentId ?? null),
    ownerSince: s.ownerSince ?? null,
    ownerSource: s.ownerSource ?? null,
    ownerReleased: s.ownerReleased === true,
    ownerVersion: finiteOr(s.ownerVersion, 0),
    // Legacy shared behaviour unless the wire says, as a boolean, that private ownership is in effect.
    ownershipEnforced: (typeof s.ownershipEnforced === 'boolean' ? s.ownershipEnforced : envelope?.ownershipEnforced) === true,
    tags: Array.isArray(s.tags) ? s.tags.filter((t): t is string => typeof t === 'string') : [],
    tagsVersion: finiteOr(s.tagsVersion, 0),
    // The list's sort key. A row the activity backfill has not reached has none: fall back to the last message (then
    // creation) rather than sorting it to the epoch.
    lastActivityAt: s.lastActivityAt ?? s.lastMessageAt ?? s.createdAt,
    lastCustomerMessageAt: s.lastCustomerMessageAt ?? null,
    lastOutboundAt: s.lastOutboundAt ?? null,
    previousSessionId: s.previousSessionId ?? null,

    // Conversation-status overhaul. A row without the field reads MEDIUM; an unknown value is MEDIUM too (never invented).
    priority: toPriority(s.priority),
    priorityPinned: s.priorityPinned === true,
    lastPreview: typeof s.lastPreview === 'string' && s.lastPreview !== '' ? s.lastPreview : null,
    lastPreviewAt: s.lastPreviewAt ?? null,
    lastPreviewSender: toSender(s.lastPreviewSender),
    lastPreviewAgentName: typeof s.lastPreviewAgentName === 'string' && s.lastPreviewAgentName.trim() ? s.lastPreviewAgentName : null,
    reopenedAt: s.reopenedAt ?? null,
    reopenCount: finiteOr(s.reopenCount, 0),
  };
}

function toPriority(p: string | null | undefined): ConversationPriority {
  const v = typeof p === 'string' ? p.trim().toUpperCase() : '';
  return v === 'LOW' || v === 'HIGH' || v === 'URGENT' ? v : 'MEDIUM';
}

function toSender(v: string | null | undefined): PreviewSender | null {
  const x = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return x === 'customer' || x === 'bot' || x === 'agent' ? x : null;
}

function finiteOr(n: number | undefined, fallback: number): number {
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

/**
 * One keyset page mapped for the store: every row is stamped with the page's `ownershipEnforced` when it carries none
 * of its own, so a row never outlives the envelope that explains it.
 */
export function mapFolderPage(page: FolderPageResponse): {
  conversations: Conversation[];
  nextCursor: string | null;
  hasMore: boolean;
  ownershipEnforced: boolean;
} {
  const envelope = { ownershipEnforced: page.ownershipEnforced === true };
  return {
    conversations: page.items.map((s) => mapSessionToConversation(s, envelope)),
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
    ownershipEnforced: envelope.ownershipEnforced,
  };
}
